import { useEffect, useState, useRef } from "react";
import { UserRound, Camera, Loader2, Facebook, Instagram, Linkedin, Crown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";

export default function Profile() {
  const { refreshUser } = useAuth();
  const { openPaywall } = usePaywall();
  const [profile, setProfile] = useState(null);
  const [sub, setSub] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [name, setName] = useState("");
  const [socials, setSocials] = useState({ facebook: "", instagram: "", linkedin: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/profile").then((res) => {
      setProfile(res.data);
      setName(res.data.name || "");
      setSocials({ facebook: "", instagram: "", linkedin: "", ...(res.data.socials || {}) });
    });
    api.get("/billing/subscription").then((res) => setSub(res.data)).catch(() => {});
  };
  useEffect(load, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open billing portal.");
      setPortalLoading(false);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      await api.post("/billing/restore");
      await refreshUser();
      load();
      toast.success("Your subscription has been restored!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No subscription found.");
    } finally {
      setRestoring(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, avatar_url: data.avatar_url }));
      await refreshUser();
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name, socials });
      setProfile(data);
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="text-center pt-32"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#2C2A29]" /></div>;

  const socialFields = [
    { key: "facebook", label: "Facebook", Icon: Facebook, ph: "https://facebook.com/your-profile" },
    { key: "instagram", label: "Instagram", Icon: Instagram, ph: "https://instagram.com/your-handle" },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin, ph: "https://linkedin.com/in/your-profile" },
  ];

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="profile-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Your profile</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">Add a picture and link your social profiles.</p>

      {/* Subscription */}
      <div className="mt-8 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6" data-testid="subscription-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <Crown className={`w-5 h-5 ${sub?.tier === "premium" ? "text-[#9B7E4E]" : "text-[#B7AE9F]"}`} strokeWidth={1.75} />
            <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E]">Subscription</p>
          </div>
          <span
            data-testid="subscription-tier-badge"
            className={`text-xs uppercase tracking-wide px-3 py-1 rounded-full border ${
              sub?.tier === "premium" ? "border-[#9B7E4E] text-[#9B7E4E]" : "border-[#D9D4CC] text-[#7A756E]"
            }`}
          >
            {sub === null ? "…" : sub.tier === "premium" ? (sub.status === "trialing" ? "Premium · Trial" : "Premium") : "Free"}
          </span>
        </div>
        {sub?.tier === "premium" ? (
          <div className="mt-4">
            {sub.is_admin && !sub.plan ? (
              <p className="font-body text-sm text-[#4A4744]">Premium access included with your admin account.</p>
            ) : sub.gift_until && !sub.plan ? (
              <p className="font-body text-sm text-[#4A4744]" data-testid="subscription-gift">
                Gifted Premium · active until {new Date(sub.gift_until).toLocaleDateString()}
              </p>
            ) : (
              <p className="font-body text-sm text-[#4A4744]" data-testid="subscription-renewal">
                {sub.plan === "premium_annual" ? "Annual plan ($49.99/yr)" : "Monthly plan ($4.99/mo)"}
                {sub.renews_at && (
                  <> · {sub.cancel_at_period_end ? "ends" : sub.status === "trialing" ? "trial ends" : "renews"}{" "}
                  {new Date(sub.renews_at).toLocaleDateString()}</>
                )}
              </p>
            )}
            {sub.has_billing && (
              <button
                data-testid="manage-billing-button"
                onClick={openPortal}
                disabled={portalLoading}
                className="mt-4 flex items-center gap-2 px-5 py-2.5 text-sm border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300 disabled:opacity-50"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Manage subscription
              </button>
            )}
            <button
              data-testid="premium-tour-replay"
              onClick={() => window.dispatchEvent(new Event("iie:premium-tour"))}
              className="mt-3 font-body text-xs text-[#7A756E] underline underline-offset-2 hover:text-[#2C2A29] transition-colors duration-300"
            >
              Take the Premium tour
            </button>
          </div>
        ) : sub ? (
          <div className="mt-4">
            <p className="font-body text-sm text-[#4A4744]">
              Unlock unlimited decrees, full-length plans, every teaching, and unlimited word studies.
            </p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                data-testid="profile-upgrade-button"
                onClick={() => openPaywall("Unlock everything with Premium — try it free for 7 days.")}
                className="flex items-center gap-2 px-5 py-2.5 text-sm bg-[#9B7E4E] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300"
              >
                <Crown className="w-4 h-4" /> {sub.trial_used ? "Upgrade to Premium" : "Try Premium free for 7 days"}
              </button>
              <button
                data-testid="profile-restore-button"
                onClick={restore}
                disabled={restoring}
                className="font-body text-xs text-[#7A756E] underline underline-offset-2 hover:text-[#2C2A29] transition-colors duration-300 disabled:opacity-50"
              >
                {restoring ? "Restoring…" : "Restore purchases"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-6 mt-10">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center">
            {profile.avatar_url ? (
              <img data-testid="profile-avatar" src={mediaUrl(profile.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              <UserRound className="w-10 h-10 text-[#B7AE9F]" />
            )}
          </div>
          <button
            data-testid="avatar-upload-button"
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#2C2A29] text-[#FAF9F6] flex items-center justify-center hover:opacity-90 transition-opacity"
            aria-label="Upload picture"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPick} data-testid="avatar-file-input" />
        </div>
        <div>
          <p className="font-body text-sm text-[#7A756E]">Recommended: square image, at least 400×400px, under 5MB.</p>
          <p className="font-body text-sm text-[#7A756E] mt-1">JPG, PNG, WEBP or GIF.</p>
        </div>
      </div>

      <div className="mt-10 space-y-5">
        <div>
          <label className="font-body text-sm text-[#4A4744] mb-2 block">Display name</label>
          <input data-testid="profile-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="font-body text-sm text-[#4A4744] mb-2 block">Email</label>
          <input className={`${inputCls} opacity-60`} value={profile.email} disabled />
        </div>

        <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] pt-2">Social profiles</p>
        {socialFields.map(({ key, label, Icon, ph }) => (
          <div key={key} className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-[#7A756E] shrink-0" />
            <input
              data-testid={`social-${key}`}
              className={inputCls}
              placeholder={ph}
              value={socials[key] || ""}
              onChange={(e) => setSocials((s) => ({ ...s, [key]: e.target.value }))}
              aria-label={label}
            />
          </div>
        ))}
      </div>

      <button
        data-testid="profile-save"
        onClick={save}
        disabled={saving}
        className="mt-8 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}
